import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../allauth/auth";
import { USER_TYPE } from "../object-actions/types/types";
import { useAppContext } from "./AppContext";

interface RoleProtectedRouteProps {
    children: React.ReactNode;
    requiredRoles: USER_TYPE[];
    requireAll?: boolean;
}

const PARTICIPATION_ROLES: USER_TYPE[] = ["athlete", "coach", "parent"];

function membershipMayBeStaleAfterClaimRole(
    availableRoles: USER_TYPE[],
    requiredRoles: USER_TYPE[],
    requireAll: boolean,
): boolean {
    if (!availableRoles.includes("payer")) {
        return false;
    }
    const hasRequiredRole = requireAll
        ? requiredRoles.every((role) => availableRoles.includes(role))
        : requiredRoles.some((role) => availableRoles.includes(role));
    if (hasRequiredRole) {
        return false;
    }
    return requiredRoles.some((role) => PARTICIPATION_ROLES.includes(role));
}

export function RoleProtectedRoute({
    children,
    requiredRoles,
    requireAll = false
}: RoleProtectedRouteProps) {
    const location = useLocation();
    const auth = useAuth();
    const { membership, refresh } = useAppContext();
    const refreshAttempted = useRef(false);
    const [accessDenied, setAccessDenied] = useState(false);

    const availableRoles = membership?.roles || [];
    const hasRequiredRole = requireAll
        ? requiredRoles.every(role => availableRoles.includes(role))
        : requiredRoles.some(role => availableRoles.includes(role));

    const stalePayerMembership = useMemo(
        () => membershipMayBeStaleAfterClaimRole(availableRoles, requiredRoles, requireAll),
        [availableRoles, requiredRoles, requireAll],
    );

    useEffect(() => {
        if (hasRequiredRole) {
            setAccessDenied(false);
            return;
        }
        if (stalePayerMembership && !refreshAttempted.current) {
            refreshAttempted.current = true;
            void refresh();
            return;
        }
        setAccessDenied(true);
    }, [hasRequiredRole, stalePayerMembership, refresh]);

    // Check if user is authenticated
    if (!auth?.data?.user) {
        const next = `next=${encodeURIComponent(location.pathname + location.search)}`;
        return <Navigate to={`/account/login?${next}`} />;
    }

    if (hasRequiredRole) {
        return <>{children}</>;
    }

    if (!accessDenied) {
        return null;
    }

    return (
        <Navigate
            to="/"
            state={{
                error: `Access denied. This page requires ${requiredRoles.join(' or ')} access.`
            }}
        />
    );
}
